import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { PlusIcon, EditIcon, TrashIcon, CreditCardIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryData, setCategoryData] = useState({ name: '', icon: '📝', color: 'gray' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/expense-categories`);
      const categoriesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
      toast.error('Kategoriler yüklenirken hata oluştu');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryData.name || !categoryData.name.trim()) {
      toast.error('Kategori adı gerekli');
      return;
    }

    try {
      const payload = {
        category_name: categoryData.name,
        category_code: categoryData.name.toUpperCase().replace(/\s+/g, '_'),
        icon: categoryData.icon || '📝',
        color: categoryData.color
      };

      if (editingCategory) {
        await axios.put(`${API}/expense-categories/${editingCategory.id}`, payload);
        toast.success('Kategori güncellendi');
      } else {
        await axios.post(`${API}/expense-categories`, payload);
        toast.success('Kategori oluşturuldu');
      }

      setDialogOpen(false);
      setCategoryData({ name: '', icon: '📝', color: 'gray' });
      setEditingCategory(null);
      await fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Kategori kaydedilirken hata oluştu';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Kategori kaydedilirken hata oluştu');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`"${category.category_name}" kategorisini silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/expense-categories/${category.id}`);
      toast.success('Kategori silindi');
      fetchCategories();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Kategori silinirken hata oluştu';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Kategori silinirken hata oluştu');
    }
  };

  const openDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryData({
        name: category.category_name,
        icon: category.icon || '📝',
        color: category.color || 'gray'
      });
    } else {
      setEditingCategory(null);
      setCategoryData({ name: '', icon: '📝', color: 'gray' });
    }
    setDialogOpen(true);
  };

  const filteredCategories = categories.filter(cat =>
    cat.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const colorOptions = [
    { value: 'gray', label: 'Gri', class: 'bg-gray-100 text-gray-800' },
    { value: 'blue', label: 'Mavi', class: 'bg-blue-100 text-blue-800' },
    { value: 'green', label: 'Yeşil', class: 'bg-green-100 text-green-800' },
    { value: 'yellow', label: 'Sarı', class: 'bg-yellow-100 text-yellow-800' },
    { value: 'red', label: 'Kırmızı', class: 'bg-red-100 text-red-800' },
    { value: 'purple', label: 'Mor', class: 'bg-purple-100 text-purple-800' },
    { value: 'pink', label: 'Pembe', class: 'bg-pink-100 text-pink-800' },
    { value: 'orange', label: 'Turuncu', class: 'bg-orange-100 text-orange-800' }
  ];

  const getColorClass = (color) => {
    const colorOption = colorOptions.find(opt => opt.value === color);
    return colorOption ? colorOption.class : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <Card className="p-6">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CreditCardIcon className="h-6 w-6 text-teal-600" />
              <h2 className="text-2xl font-bold text-gray-900">Kredi Kartı Harcama Kategorileri</h2>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => openDialog()} 
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Yeni Kategori Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>
                    {editingCategory ? 'Kategori Güncelle' : 'Yeni Kredi Kartı Kategorisi'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="categoryName">Kategori Adı *</Label>
                    <Input
                      id="categoryName"
                      value={categoryData.name}
                      onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
                      placeholder="Örn: Pazarlama, IT Hizmetleri"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="categoryIcon">Icon (emoji)</Label>
                    <Input
                      id="categoryIcon"
                      value={categoryData.icon}
                      onChange={(e) => setCategoryData({ ...categoryData, icon: e.target.value })}
                      placeholder="📝"
                      className="mt-1"
                      maxLength={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="categoryColor">Renk</Label>
                    <select
                      id="categoryColor"
                      value={categoryData.color}
                      onChange={(e) => setCategoryData({ ...categoryData, color: e.target.value })}
                      className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm"
                    >
                      {colorOptions.map(color => (
                        <option key={color.value} value={color.value}>{color.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button 
                      variant="outline" 
                      onClick={() => setDialogOpen(false)}
                    >
                      İptal
                    </Button>
                    <Button 
                      onClick={handleSaveCategory}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {editingCategory ? 'Güncelle' : 'Oluştur'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="mb-4">
            <Input
              placeholder="Kategori ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <CreditCardIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchTerm ? 'Arama sonucu bulunamadı' : 'Henüz kredi kartı kategorisi eklenmemiş'}
            </p>
            <p className="text-gray-400 mt-2">
              {searchTerm ? 'Farklı bir arama terimi deneyin' : 'Başlamak için "Yeni Kategori Ekle" butonuna tıklayın'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCategories.map((category) => (
              <Card key={category.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-3xl">{category.icon || '📝'}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{category.category_name}</h3>
                        <Badge className={getColorClass(category.color)}>
                          {category.category_code}
                        </Badge>
                        {category.is_system && (
                          <Badge variant="outline" className="text-xs">
                            Sistem
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDialog(category)}
                      className="text-teal-600 hover:text-teal-700 hover:border-teal-600"
                      disabled={category.is_system}
                    >
                      <EditIcon className="h-4 w-4 mr-1" />
                      Düzenle
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCategory(category)}
                      className="text-red-600 hover:text-red-700 hover:border-red-600"
                      disabled={category.is_system}
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Sil
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CategoryManagement;
